import { useQuery } from '@tanstack/react-query';
import { getVatRates } from '@/core/api';
import { Spinner } from '../Spinner';
import { useTranslation } from 'react-i18next';
import { normalizeDateFE } from '@/core/normalization';

interface Props {
  country: string;
  date: Date | undefined;
  className?: string;
}

const VatRateInfoBlock = ({ country, date, className = '' }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['SellerVatRates'],
    queryFn: () => getVatRates(),
  });

  const filteredVatRates = data
    ?.filter((item) => item.country.short_name === country)
    .filter(
      (item) =>
        date &&
        (!item.effective_from ||
          normalizeDateFE(item.effective_from) <= date) &&
        (!item.effective_till || normalizeDateFE(item.effective_till) >= date)
    )
    .reduce<{
      standard: string[];
      reduced: string[];
      zero: string[];
    }>(
      (acc, item) => {
        const optionValue = item.vat_rate_option.option_value;

        if (optionValue === 'HIGH') {
          acc.standard.push(`${parseFloat(item.vat_rate).toString()}%`);
        } else if (optionValue === 'ZERO') {
          acc.zero.push(`${parseFloat(item.vat_rate).toString()}%`);
        } else {
          acc.reduced.push(`${parseFloat(item.vat_rate).toString()}%`);
        }

        return acc;
      },
      { standard: [], reduced: [], zero: [] }
    );

  return isLoading ? (
    <Spinner />
  ) : (!!filteredVatRates?.standard.length ||
      !!filteredVatRates?.reduced.length) &&
    date ? (
    <div className={`pb-2 ${className}`}>
      <p className='space-x-1'>
        <span>{country as unknown as string}</span>
        <span>({t(country as unknown as string)})</span>
        {!!filteredVatRates?.standard.length && (
          <>
            <span>{t('standard VAT rates')}:</span>
            <span>{filteredVatRates?.standard.join(', ')}</span>
          </>
        )}
      </p>
      {!!filteredVatRates?.reduced.length && (
        <p>
          <span>{t('Reduced rates')}:</span>{' '}
          {filteredVatRates?.reduced.join(', ')}
        </p>
      )}
    </div>
  ) : null;
};

export default VatRateInfoBlock;
